import axios from "axios";
import moment from "moment-timezone";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { NotificationManager } from "react-notifications";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import OptionalOption from "../../../components/FilterComponents/OptionalOption";
import useAuth from "../../../hooks/useAuth";
import { BASE_URL } from "../../../utils/Element";
import { stateOptions } from "../../../utils/options";

/* ---------------- FIELD OPTIONS ---------------- */
const FIELD_OPTIONS = {
    "Property / Tenant Name": ["Is", "Contains", "Is Empty", "Is Not Empty"],
    "Property Type": ["Is", "Is Not", "Is Any", "Is All", "Is Empty", "Is Not Empty"],
    "Landlord Responsibilities": ["Is", "Contains", "Is Empty", "Is Not Empty"],
    State: ["Is", "Is Not", "Is Any", "Is All", "Is Empty", "Is Not Empty"],
    "Lease Term Remaining": ["Is", "Greater Than", "Less Than", "Between", "Is Empty", "Is Not Empty"],
    "Asking Cap Rate": ["Is", "Greater Than", "Less Than", "Between", "Is Empty", "Is Not Empty"],
    "Asking Price": ["Is", "Greater Than", "Less Than", "Between", "Is Empty", "Is Not Empty"],
    "Price per SF": ["Is", "Greater Than", "Less Than", "Between", "Is Empty", "Is Not Empty"],
    "Rent per SF": ["Is", "Greater Than", "Less Than", "Between", "Is Empty", "Is Not Empty"],
    "Total GLA": ["Is", "Greater Than", "Less Than", "Between", "Is Empty", "Is Not Empty"],
    "Pipeline User": ["Is", "Is Not", "Is Any", "Is All", "Exclude Any"],
    Demographics: ["Is", "Contains", "Is Empty", "Is Not Empty"],
    "Traffic Counts": ["Is", "Greater Than", "Less Than", "Between", "Is Empty", "Is Not Empty"],
    "Last Update Date": [
        "On", "Before", "After", "Between", "In Last X Days", "Longer Than X Days",
        "Is Empty", "Is Not Empty"
    ],
    "Last Contacted": [
        "On", "Before", "After", "Between", "In Last X Days", "Longer Than X Days",
        "Is Empty", "Is Not Empty"
    ],
    "Buyer Tags": ["Is", "Is Not", "Is Any", "Is All", "Is Empty", "Is Not Empty"],

    "Exclude Buyer": ["Matching", "Is Empty", "Is Not Empty"],
};

/* --------- FIELDS THAT USE DROPDOWN --------- */
const DROPDOWN_FIELDS = {
    State: "state",
    "Property Type": "property_types",
    "Buyer Tags": "buyer_tags",
    "Pipeline User": "users",
    "Property / Tenant Name": "tenant",
};

/* Sub-filter fields for Exclude Buyer */
const EXCLUDE_BUYER_SUB_FIELDS = {
    "Property / Tenant Name": ["Is", "Contains", "Is Empty", "Is Not Empty"],
    "Property Type": ["Is", "Is Not", "Is Any", "Is All", "Is Empty", "Is Not Empty"],
    "Landlord Responsibilities": ["Is", "Contains", "Is Empty", "Is Not Empty"],
    State: ["Is", "Is Not", "Is Any", "Is All", "Is Empty", "Is Not Empty"],
    "Lease Term Remaining": ["Is", "Greater Than", "Less Than", "Between", "Is Empty", "Is Not Empty"],
    "Asking Cap Rate": ["Is", "Greater Than", "Less Than", "Between", "Is Empty", "Is Not Empty"],
    "Asking Price": ["Is", "Greater Than", "Less Than", "Between", "Is Empty", "Is Not Empty"],
    "Price per SF": ["Is", "Greater Than", "Less Than", "Between", "Is Empty", "Is Not Empty"],
    "Rent per SF": ["Is", "Greater Than", "Less Than", "Between", "Is Empty", "Is Not Empty"],
    "Total GLA": ["Is", "Greater Than", "Less Than", "Between", "Is Empty", "Is Not Empty"],
    Demographics: ["Is", "Contains", "Is Empty", "Is Not Empty"],
    "Pipeline User": ["Is", "Is Not", "Is Any", "Is All", "Exclude Any"],
    "Traffic Counts": ["Is", "Greater Than", "Less Than", "Between", "Is Empty", "Is Not Empty"],
    "Last Update Date": [
        "On", "Before", "After", "Between", "In Last X Days", "Longer Than X Days",
        "Is Empty", "Is Not Empty"
    ],
    "Last Contacted": [
        "On", "Before", "After", "Between", "In Last X Days", "Longer Than X Days",
        "Is Empty", "Is Not Empty"
    ],
    "Buyer Tags": ["Is", "Is Not", "Is Any", "Is All", "Is Empty", "Is Not Empty"],
};

/* ---------------- JOIN OPTIONS ---------------- */
const JOIN_OPTIONS = [
    { label: "AND", value: "AND" },
    { label: "OR", value: "OR" },
];

const selectPortalStyles = {
    menuPortal: (base) => ({
        ...base,
        zIndex: 999999   // Very high z-index
    }),
    menu: (base) => ({
        ...base,
        zIndex: 999999,
    }),
    control: (base) => ({
        ...base,
        minHeight: "38px",
    }),
};

const DATE_FIELDS = ["Last Update Date", "Last Contacted"];
const NUMERIC_FIELDS = [
    "Lease Term Remaining",
    "Asking Cap Rate",
    "Asking Price",
    "Price per SF",
    "Rent per SF",
    "Total GLA",
    "Traffic Counts",
];

const PREFIX_MAP = {
    "Last Update Date": "last_update_",
    "Last Contacted": "last_contacted_",
    "Lease Term Remaining": "lease_term_",
    "Asking Cap Rate": "cap_rate_",
    "Asking Price": "asking_price_",
    "Price per SF": "price_per_sf_",
    "Rent per SF": "rent_per_sf_",
    "Total GLA": "gla_",
    "Traffic Counts": "traffic_",
};

const EXCLUDE_BUYER_FIELD = "Exclude Buyer";
const EXCLUDE_BUYER_SUB_DATE_FIELDS = ["Last Update Date", "Last Contacted"];
const EXCLUDE_BUYER_SUB_PREFIX_MAP = {
    "Last Update Date": "exclude_last_update_",
    "Last Contacted": "exclude_last_contacted_",
};

/* ================== SUB FILTER VALUE SELECT (FIXED) ================== */
const SubFilterValueSelect = ({
    subFilter,
    groupIndex,
    filterIndex,
    subIndex,
    dropdownOptions,
    setDropdownOptions,
    fetchDropdownOptions,
    updateSubFilter,
}) => {
    const isMulti = ["Is Any", "Is All"].includes(subFilter.operator);
    const field = subFilter.field;
    const isStaticState = DROPDOWN_FIELDS[field] === "state";

    // Load options for API-based dropdowns
    useEffect(() => {
        if (field && DROPDOWN_FIELDS[field] && !isStaticState && !dropdownOptions[field]) {
            fetchDropdownOptions(field).then((opts) => {
                setDropdownOptions((prev) => ({ ...prev, [field]: opts }));
            });
        }
    }, [field, dropdownOptions, setDropdownOptions, fetchDropdownOptions, isStaticState]);

    // Get correct options (Fixed for State)
    let options = [];
    if (isStaticState) {
        options = stateOptions;
    } else {
        options = dropdownOptions[field] || [];
    }

    const selectedValue = isMulti
        ? options.filter((opt) => (subFilter.value || []).includes(opt.value))
        : options.find((opt) => opt.value === subFilter.value);

    return (
        <Select
            className="flex-1 dark-H body-N"
            placeholder="Select Value"
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={selectPortalStyles}
            menuShouldBlockScroll={true}
            isSearchable
            isMulti={isMulti}
            options={options}
            value={selectedValue || null}
            onChange={(selected) => {
                const newValue = isMulti
                    ? selected?.map((opt) => opt.value) || []
                    : selected?.value || "";
                updateSubFilter(groupIndex, filterIndex, subIndex, "value", newValue);
            }}
        />
    );
};

function BuyerAdvanceFilterData({ filterData, onSetFilterData, isSidebarOpen }) {
    const [config] = useAuth();

    const [groups, setGroups] = useState(
        filterData && filterData.length > 0
            ? filterData
            : [{ id: Date.now(), condition: "AND", filters: [{ join: null, field: "", operator: "", value: "" }] }]
    );

    const [dropdownOptions, setDropdownOptions] = useState({});
    const [tenantOptions, setTenantOptions] = useState([]);

    // Fetch Tenants
    const fetchTenants = () => {
        axios
            .get(`${BASE_URL}/get-datalisting?list=tenant`, config)
            .then((res) => {
                const tenants = res?.data?.data?.tenant || [];
                const formatted = tenants.map((el) => ({
                    value: el?.id,
                    label: el?.tenant_name,
                }));
                setTenantOptions(formatted);
            })
            .catch((err) => {
                if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
                    NotificationManager.error(err.response?.data?.message);
                }
            });
    };

    useEffect(() => {
        if (isSidebarOpen) {
            fetchTenants();
        }
    }, [isSidebarOpen]);

    useEffect(() => {
        if (isSidebarOpen && filterData && filterData.length > 0) {
            setGroups(filterData);

            filterData.forEach((group) => {
                group.filters.forEach((filter) => {
                    if (filter.field && DROPDOWN_FIELDS[filter.field] && !dropdownOptions[filter.field]) {
                        fetchDropdownOptions(filter.field).then((opts) =>
                            setDropdownOptions((prev) => ({ ...prev, [filter.field]: opts }))
                        );
                    }
                });
            });
        }
    }, [isSidebarOpen, filterData]);

    const updateState = (updated) => {
        setGroups(updated);
        onSetFilterData(updated);
    };

    /* ---------------- API FETCHER ---------------- */
    const fetchDropdownOptions = async (field, search = "") => {
        const list = DROPDOWN_FIELDS[field];
        if (!list) return [];

        if (list === "state") return stateOptions;
        if (list === "tenant") return tenantOptions;

        try {
            const res = await axios.get(
                `${BASE_URL}/get-datalisting?list=${list}&role_id=3&search=${search}`,
                config
            );

            const data = res?.data?.data || {};

            if (list === "property_types") {
                return data.property_types?.map((el) => ({
                    label: el.name || el.type,
                    value: el.id || el.name,
                })) || [];
            }
            if (list === "users") {
                return data.map((el) => ({
                    label: `${el.first_name} ${el.last_name}`,
                    value: el.id,
                })) || [];
            }
            if (list === "buyer_tags") {
                return data.buyer_tags?.map((el) => ({
                    label: el.tag_name || el.name,
                    value: el.id || el.name,
                })) || [];
            }
            return [];
        } catch (err) {
            console.error(`Error fetching ${field}:`, err);
            if (err.response?.data?.message !== "Too Many Attempts.") {
                NotificationManager.error(err.response?.data?.message || "Failed to load options");
            }
            return [];
        }
    };

    /* ---------------- ROW HANDLERS ---------------- */
    const addFilterRow = (groupIndex) => {
        const updated = [...groups];
        updated[groupIndex].filters.push({ join: "AND", field: "", operator: "", value: "" });
        updateState(updated);
    };

    const updateRow = (groupIndex, filterIndex, key, value) => {
        const updated = [...groups];
        const filter = updated[groupIndex].filters[filterIndex];

        if (key === "field") {
            const newField = value?.value ?? value;
            filter.field = newField;
            filter.operator = "";

            const isDateField = DATE_FIELDS.includes(newField);
            const isNumericField = NUMERIC_FIELDS.includes(newField);
            const isExcludeBuyerField = newField === EXCLUDE_BUYER_FIELD;

            if (isDateField || isNumericField) {
                const prefix = PREFIX_MAP[newField] || "";
                filter.value = {
                    [`${prefix}count`]: "",
                    [`${prefix}start_count`]: "",
                    [`${prefix}end_count`]: "",
                };
            } else if (isExcludeBuyerField) {
                filter.value = { subFilters: [{ field: "", operator: "", value: "" }] };
            } else {
                filter.value = "";
            }

            if (DROPDOWN_FIELDS[newField] && !dropdownOptions[newField]) {
                fetchDropdownOptions(newField).then((opts) =>
                    setDropdownOptions((prev) => ({ ...prev, [newField]: opts }))
                );
            }
        } else if (key === "operator") {
            const newOp = value?.value ?? value;
            filter.operator = newOp;

            const isExcludeBuyerField = filter.field === EXCLUDE_BUYER_FIELD;

            if (isExcludeBuyerField) {
                if (["Matching", "Is Empty", "Is Not Empty"].includes(newOp)) {
                    filter.value = {
                        subFilters: filter.value?.subFilters?.length
                            ? filter.value.subFilters
                            : [{ field: "", operator: "", value: "" }]
                    };
                } else {
                    filter.value = "";
                }
            } else if (DATE_FIELDS.includes(filter.field) || NUMERIC_FIELDS.includes(filter.field)) {
                const prefix = PREFIX_MAP[filter.field] || "";
                filter.value = {
                    [`${prefix}count`]: "",
                    [`${prefix}start_count`]: "",
                    [`${prefix}end_count`]: "",
                };
            } else {
                filter.value = ["Is Any", "Is All"].includes(newOp) ? [] : "";
            }
        } else {
            filter[key] = value?.value ?? value;
        }

        updateState(updated);
    };

    const handleFilterChange = (groupIndex, filterIndex, subKey, subValue) => {
        const updated = [...groups];
        const filter = updated[groupIndex].filters[filterIndex];
        filter.value = { ...filter.value || {}, [subKey]: subValue };
        updateState(updated);
    };

    const addSubFilter = (groupIndex, filterIndex) => {
        const updated = [...groups];
        const filter = updated[groupIndex].filters[filterIndex];
        if (!filter.value) filter.value = {};
        if (!filter.value.subFilters) filter.value.subFilters = [];
        filter.value.subFilters.push({ field: "", operator: "", value: "" });
        updateState(updated);
    };

    const updateSubFilter = (groupIndex, filterIndex, subIndex, key, value) => {
        const updated = [...groups];
        const filter = updated[groupIndex].filters[filterIndex];
        const subFilter = filter.value.subFilters[subIndex];

        if (key === "field") {
            const newField = value?.value ?? value;
            subFilter.field = newField;
            subFilter.operator = "";

            const isDateField = EXCLUDE_BUYER_SUB_DATE_FIELDS.includes(newField);
            if (isDateField) {
                const prefix = EXCLUDE_BUYER_SUB_PREFIX_MAP[newField] || "";
                subFilter.value = {
                    [`${prefix}count`]: "",
                    [`${prefix}start_count`]: "",
                    [`${prefix}end_count`]: "",
                };
            } else {
                subFilter.value = "";
            }
        } else if (key === "operator") {
            const newOp = value?.value ?? value;
            subFilter.operator = newOp;

            const isMultiOp = ["Is Any", "Is All"].includes(newOp);
            const isDateField = EXCLUDE_BUYER_SUB_DATE_FIELDS.includes(subFilter.field);

            if (isDateField) {
                const prefix = EXCLUDE_BUYER_SUB_PREFIX_MAP[subFilter.field] || "";
                subFilter.value = {
                    [`${prefix}count`]: "",
                    [`${prefix}start_count`]: "",
                    [`${prefix}end_count`]: "",
                };
            } else {
                subFilter.value = isMultiOp ? [] : "";
            }
        } else {
            subFilter[key] = value?.value ?? value;
        }

        updateState(updated);
    };

    const removeSubFilter = (groupIndex, filterIndex, subIndex) => {
        const updated = [...groups];
        const filter = updated[groupIndex].filters[filterIndex];
        if (filter.value?.subFilters) {
            filter.value.subFilters.splice(subIndex, 1);
            if (filter.value.subFilters.length === 0) {
                filter.value.subFilters = [{ field: "", operator: "", value: "" }];
            }
        }
        updateState(updated);
    };

    const handleSubFilterDateChange = (groupIndex, filterIndex, subIndex, subKey, subValue) => {
        const updated = [...groups];
        const filter = updated[groupIndex].filters[filterIndex];
        const subFilter = filter.value.subFilters[subIndex];
        subFilter.value = { ...subFilter.value || {}, [subKey]: subValue };
        updateState(updated);
    };

    const removeRow = (groupIndex, filterIndex) => {
        const updated = [...groups];
        if (updated[groupIndex].filters.length === 1) {
            if (updated.length > 1) {
                updated.splice(groupIndex, 1);
            } else {
                updated[groupIndex].filters[0] = { join: null, field: "", operator: "", value: "" };
            }
        } else {
            updated[groupIndex].filters.splice(filterIndex, 1);
        }
        updateState(updated);
    };

    /* ---------------- RENDER SUB-FILTERS ---------------- */
    const renderSubFilters = (groupIndex, filterIndex, filter) => {
        if (filter.field !== EXCLUDE_BUYER_FIELD) return null;
        if (!["Matching", "Is Empty", "Is Not Empty"].includes(filter.operator)) return null;

        const subFilters = filter.value?.subFilters || [{ field: "", operator: "", value: "" }];

        return (
            <div className="mt-3 ml-4 pl-4 border-l-2 border-gray-300 space-y-3">
                {subFilters.map((subFilter, subIndex) => (
                    <div key={subIndex} className="space-y-2">
                        <div className="flex items-center gap-2">
                            {subIndex > 0 && <span className="text-sm text-gray-500 w-12">AND</span>}

                            <Select
                                className="flex-1 dark-H body-N"
                                placeholder="Select Field"
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={selectPortalStyles}
                                menuShouldBlockScroll={true}
                                isSearchable
                                options={Object.keys(EXCLUDE_BUYER_SUB_FIELDS).sort((a, b) => a.localeCompare(b)).map((f) => ({ label: f, value: f }))}
                                value={subFilter.field ? { label: subFilter.field, value: subFilter.field } : null}
                                onChange={(e) => updateSubFilter(groupIndex, filterIndex, subIndex, "field", e)}
                            />

                            <Select
                                className="flex-1 dark-H body-N"
                                placeholder="Condition"
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={selectPortalStyles}
                                menuShouldBlockScroll={true}
                                isSearchable
                                isDisabled={!subFilter.field}
                                options={
                                    subFilter.field
                                        ? EXCLUDE_BUYER_SUB_FIELDS[subFilter.field].map((c) => ({ label: c, value: c }))
                                        : []
                                }
                                value={subFilter.operator ? { label: subFilter.operator, value: subFilter.operator } : null}
                                onChange={(e) => updateSubFilter(groupIndex, filterIndex, subIndex, "operator", e)}
                            />

                            <button
                                className="text-red-500 hover:text-red-700 px-2"
                                onClick={() => removeSubFilter(groupIndex, filterIndex, subIndex)}
                            >
                                ✕
                            </button>
                        </div>

                        {subFilter.operator && !["Is Empty", "Is Not Empty"].includes(subFilter.operator) && (
                            <div className="flex items-center gap-2 ml-12">
                                {(DROPDOWN_FIELDS[subFilter.field] || subFilter.field === "State") ? (
                                    <SubFilterValueSelect
                                        subFilter={subFilter}
                                        groupIndex={groupIndex}
                                        filterIndex={filterIndex}
                                        subIndex={subIndex}
                                        dropdownOptions={dropdownOptions}
                                        setDropdownOptions={setDropdownOptions}
                                        fetchDropdownOptions={fetchDropdownOptions}
                                        updateSubFilter={updateSubFilter}
                                    />
                                ) : (
                                    <input
                                        className="flex-1 border border-[#f4f4f4] px-2 rounded-[8px] h-[38px] m-0 dark-H body-N outline-none shadow-[0px_0px_20px_0px_#007d880d] focus:border-[#f4f4f4] focus:ring-0"
                                        placeholder="Enter Value"
                                        value={subFilter.value || ""}
                                        onChange={(e) => updateSubFilter(groupIndex, filterIndex, subIndex, "value", e.target.value)}
                                    />
                                )}
                            </div>
                        )}

                        {/* Date fields inside sub-filter */}
                        {subFilter.operator && !["Is Empty", "Is Not Empty"].includes(subFilter.operator) &&
                            EXCLUDE_BUYER_SUB_DATE_FIELDS.includes(subFilter.field) && (
                                <div className="mt-2 ml-12">
                                    {(() => {
                                        const operator = subFilter.operator;
                                        const prefix = EXCLUDE_BUYER_SUB_PREFIX_MAP[subFilter.field];
                                        
                                        if (["In Last X Days", "Longer Than X Days"].includes(operator)) {
                                            return (
                                                <input
                                                    type="number"
                                                    className="dark-H body-N w-full"
                                                    placeholder="Enter number of days"
                                                    min={0}
                                                    value={subFilter.value?.[`${prefix}count`] || ""}
                                                    onChange={(e) =>
                                                        handleSubFilterDateChange(
                                                            groupIndex, filterIndex, subIndex,
                                                            `${prefix}count`,
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            );
                                        } else if (["On", "Before", "After"].includes(operator)) {
                                            return (
                                                <DatePicker
                                                    className="dark-H body-N w-full"
                                                    dateFormat="MM/dd/yyyy"
                                                    placeholderText="MM/DD/YYYY"
                                                    maxDate={new Date()}
                                                    selected={
                                                        subFilter.value?.[`${prefix}count`]
                                                            ? moment.tz(subFilter.value[`${prefix}count`], "America/New_York").toDate()
                                                            : null
                                                    }
                                                    onChange={(date) => {
                                                        const val = date ? moment.tz(date, "America/New_York").format("YYYY-MM-DD") : "";
                                                        handleSubFilterDateChange(groupIndex, filterIndex, subIndex, `${prefix}count`, val);
                                                    }}
                                                />
                                            );
                                        } else if (operator === "Between") {
                                            return (
                                                <div className="flex flex-row gap-2">
                                                    <DatePicker
                                                        className="dark-H body-N"
                                                        dateFormat="MM/dd/yyyy"
                                                        placeholderText="Start Date"
                                                        maxDate={new Date()}
                                                        selected={
                                                            subFilter.value?.[`${prefix}start_count`]
                                                                ? moment.tz(subFilter.value[`${prefix}start_count`], "America/New_York").toDate()
                                                                : null
                                                        }
                                                        onChange={(date) => {
                                                            const val = date ? moment.tz(date, "America/New_York").format("YYYY-MM-DD") : "";
                                                            handleSubFilterDateChange(groupIndex, filterIndex, subIndex, `${prefix}start_count`, val);
                                                        }}
                                                    />
                                                    <DatePicker
                                                        className="dark-H body-N"
                                                        dateFormat="MM/dd/yyyy"
                                                        placeholderText="End Date"
                                                        maxDate={new Date()}
                                                        selected={
                                                            subFilter.value?.[`${prefix}end_count`]
                                                                ? moment.tz(subFilter.value[`${prefix}end_count`], "America/New_York").toDate()
                                                                : null
                                                        }
                                                        onChange={(date) => {
                                                            const val = date ? moment.tz(date, "America/New_York").format("YYYY-MM-DD") : "";
                                                            handleSubFilterDateChange(groupIndex, filterIndex, subIndex, `${prefix}end_count`, val);
                                                        }}
                                                    />
                                                </div>
                                            );
                                        }
                                        return null;
                                    })()}
                                </div>
                            )}
                    </div>
                ))}

                <button className="green-H head-5 text-sm mt-2" onClick={() => addSubFilter(groupIndex, filterIndex)}>
                    + Add Criteria
                </button>
            </div>
        );
    };

    /* ---------------- RENDER VALUE INPUT ---------------- */
    const renderValueInput = (groupIndex, filterIndex, filter) => {
        if (!filter.operator || ["Is Empty", "Is Not Empty"].includes(filter.operator)) return null;

        const isDateField = DATE_FIELDS.includes(filter.field);
        const isNumericField = NUMERIC_FIELDS.includes(filter.field);

        if (isNumericField) {
            const prefix = PREFIX_MAP[filter.field] || "";
            return (
                <div className="mt-2 pl-2">
                    <OptionalOption
                        activeTab="date"
                        type="number"
                        title={filter.field}
                        autoRange={filter.operator === "Between"}
                        onSetLastDate={(value) =>
                            handleFilterChange(groupIndex, filterIndex, `${prefix}count`, value)
                        }
                        onSetDateRange1={(value) =>
                            handleFilterChange(groupIndex, filterIndex, `${prefix}start_count`, value)
                        }
                        onSetDateRange2={(value) =>
                            handleFilterChange(groupIndex, filterIndex, `${prefix}end_count`, value)
                        }
                        date={filter.value?.[`${prefix}count`]}
                        date2={filter.value?.[`${prefix}start_count`]}
                        date3={filter.value?.[`${prefix}end_count`]}
                    />
                </div>
            );
        }

        if (isDateField) {
            return (
                <div className="mt-2 pl-2">
                    {(() => {
                        const prefix = PREFIX_MAP[filter.field];
                        const operator = filter.operator;
                        if (["In Last X Days", "Longer Than X Days"].includes(operator)) {
                            return (
                                <input
                                    type="number"
                                    className="dark-H body-N w-full"
                                    placeholder="Enter number of days"
                                    min={0}
                                    value={filter.value?.[`${prefix}count`] || ""}
                                    onChange={(e) => handleFilterChange(groupIndex, filterIndex, `${prefix}count`, e.target.value)}
                                    onWheel={(e) => e.target.blur()}
                                />
                            );
                        } else if (["On", "Before", "After"].includes(operator)) {
                            return (
                                <DatePicker
                                    className="dark-H body-N w-full"
                                    dateFormat="MM/dd/yyyy"
                                    placeholderText="MM/DD/YYYY"
                                    maxDate={new Date()}
                                    selected={
                                        filter.value?.[`${prefix}count`]
                                            ? moment.tz(filter.value[`${prefix}count`], "America/New_York").toDate()
                                            : null
                                    }
                                    onChange={(date) => {
                                        const val = date ? moment.tz(date, "America/New_York").format("YYYY-MM-DD") : "";
                                        handleFilterChange(groupIndex, filterIndex, `${prefix}count`, val);
                                    }}
                                />
                            );
                        } else if (operator === "Between") {
                            return (
                                <div className="flex flex-row gap-2">
                                    <DatePicker
                                        className="dark-H body-N"
                                        dateFormat="MM/dd/yyyy"
                                        placeholderText="Start Date"
                                        maxDate={new Date()}
                                        selected={
                                            filter.value?.[`${prefix}start_count`]
                                                ? moment.tz(filter.value[`${prefix}start_count`], "America/New_York").toDate()
                                                : null
                                        }
                                        onChange={(date) => {
                                            const val = date ? moment.tz(date, "America/New_York").format("YYYY-MM-DD") : "";
                                            handleFilterChange(groupIndex, filterIndex, `${prefix}start_count`, val);
                                        }}
                                    />
                                    <DatePicker
                                        className="dark-H body-N"
                                        dateFormat="MM/dd/yyyy"
                                        placeholderText="End Date"
                                        maxDate={new Date()}
                                        selected={
                                            filter.value?.[`${prefix}end_count`]
                                                ? moment.tz(filter.value[`${prefix}end_count`], "America/New_York").toDate()
                                                : null
                                        }
                                        onChange={(date) => {
                                            const val = date ? moment.tz(date, "America/New_York").format("YYYY-MM-DD") : "";
                                            handleFilterChange(groupIndex, filterIndex, `${prefix}end_count`, val);
                                        }}
                                    />
                                </div>
                            );
                        }
                        return null;
                    })()}
                </div>
            );
        }

        if (filter.field === "Property / Tenant Name" && filter.operator !== "Contains") {
            const isMulti = ["Is Any", "Is All"].includes(filter.operator);
            return (
                <Select
                    className="flex-1 dark-H body-N"
                    placeholder="Select Tenant"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectPortalStyles}
                    menuShouldBlockScroll={true}
                    isSearchable
                    isMulti={isMulti}
                    options={tenantOptions}
                    value={
                        isMulti
                            ? tenantOptions.filter((opt) => (filter.value || []).includes(opt.value))
                            : tenantOptions.find((opt) => opt.value === filter.value)
                    }
                    onChange={(selected) => {
                        const newValue = isMulti
                            ? selected?.map((opt) => opt.value) || []
                            : selected?.value || "";
                        updateRow(groupIndex, filterIndex, "value", newValue);
                    }}
                />
            );
        }

        if (DROPDOWN_FIELDS[filter.field] && filter.operator !== "Contains") {
            const isMulti = ["Is Any", "Is All"].includes(filter.operator);
            const isStatic = DROPDOWN_FIELDS[filter.field] === "state";
            const Component = isStatic ? Select : AsyncSelect;
            const fieldOptions = dropdownOptions[filter.field] || [];

            const selectedValue = isMulti
                ? fieldOptions.filter((opt) => (filter.value || []).includes(opt.value))
                : fieldOptions.find((opt) => opt.value === filter.value);

            return (
                <Component
                    className="flex-1 dark-H body-N"
                    placeholder="Select Value"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectPortalStyles}
                    menuShouldBlockScroll={true}
                    isSearchable
                    isMulti={isMulti}
                    options={isStatic ? fieldOptions : undefined}
                    defaultOptions={!isStatic ? fieldOptions : undefined}
                    loadOptions={
                        !isStatic
                            ? async (inputValue) => {
                                const opts = await fetchDropdownOptions(filter.field, inputValue);
                                setDropdownOptions((prev) => ({ ...prev, [filter.field]: opts }));
                                return opts;
                            }
                            : undefined
                    }
                    value={selectedValue}
                    onChange={(selected) => {
                        const newValue = isMulti
                            ? selected?.map((opt) => opt.value) || []
                            : selected?.value || null;
                        updateRow(groupIndex, filterIndex, "value", newValue);
                    }}
                />
            );
        }

        return (
            <input
                className="flex-1 border border-[#f4f4f4] px-2 rounded-[8px] h-[38px] m-0 dark-H body-N outline-none shadow-[0px_0px_20px_0px_#007d880d] focus:border-[#f4f4f4] focus:ring-0"
                placeholder="Enter Value"
                value={filter.value || ""}
                onChange={(e) => updateRow(groupIndex, filterIndex, "value", e.target.value)}
            />
        );
    };

    /* ---------------- MAIN RENDER ---------------- */
    return (
        <div className="space-y-6 text-black">
            {groups.map((group, groupIndex) => (
                <div key={group.id} className="bg-white space-y-2">
                    {groupIndex > 0 && (
                        <div className="text-sm font-medium mb-2 text-gray-600">{group.condition} Group</div>
                    )}

                    {group.filters.map((filter, index) => (
                        <div key={index}>
                            <div className="flex items-center gap-2">
                                {index > 0 && (
                                    <Select
                                        className="w-24 dark-H body-N"
                                        placeholder="Join"
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        styles={selectPortalStyles}
                                        menuShouldBlockScroll={true}
                                        options={JOIN_OPTIONS}
                                        value={filter.join ? { label: filter.join, value: filter.join } : null}
                                        onChange={(e) => updateRow(groupIndex, index, "join", e.value)}
                                    />
                                )}

                                <Select
                                    className="flex-1 dark-H body-N"
                                    placeholder="Select Field"
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={selectPortalStyles}
                                    menuShouldBlockScroll={true}
                                    isSearchable
                                    options={Object.keys(FIELD_OPTIONS).sort((a, b) => a.localeCompare(b)).map((f) => ({ label: f, value: f }))}
                                    value={filter.field ? { label: filter.field, value: filter.field } : null}
                                    onChange={(e) => updateRow(groupIndex, index, "field", e)}
                                />

                                <Select
                                    className="flex-1 dark-H body-N"
                                    placeholder="Condition"
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={selectPortalStyles}
                                    menuShouldBlockScroll={true}
                                    isSearchable
                                    isDisabled={!filter.field}
                                    options={
                                        filter.field
                                            ? FIELD_OPTIONS[filter.field].map((c) => ({ label: c, value: c }))
                                            : []
                                    }
                                    value={filter.operator ? { label: filter.operator, value: filter.operator } : null}
                                    onChange={(e) => updateRow(groupIndex, index, "operator", e)}
                                />

                                {filter.field !== EXCLUDE_BUYER_FIELD &&
                                    !DATE_FIELDS.includes(filter.field) &&
                                    !NUMERIC_FIELDS.includes(filter.field) &&
                                    renderValueInput(groupIndex, index, filter)}

                                <button
                                    className="text-red-500 hover:text-red-700 px-2"
                                    onClick={() => removeRow(groupIndex, index)}
                                >
                                    ✕
                                </button>
                            </div>

                            {(DATE_FIELDS.includes(filter.field) ||
                                NUMERIC_FIELDS.includes(filter.field)) && (
                                    <div className="mt-3 ml-2 w-full">
                                        {renderValueInput(groupIndex, index, filter)}
                                    </div>
                                )}

                            {renderSubFilters(groupIndex, index, filter)}
                        </div>
                    ))}

                    <button className="green-H head-5 mt-2" onClick={() => addFilterRow(groupIndex)}>
                        + Add Filter
                    </button>
                </div>
            ))}
        </div>
    );
}

export default BuyerAdvanceFilterData;